import { AnimatePresence, motion } from "motion/react";
import { Moon, Sun } from "lucide-react";
import { useTheme } from "../hooks/useTheme";

type ThemeToggleProps = {
  className?: string;
  /** Light chrome for dark / cinematic surfaces (e.g. Waitlist). */
  tone?: "default" | "onDark";
};

/**
 * Sun / moon icon toggle. Icon rotates 360° + cross-fades over ~300ms.
 */
export function ThemeToggle({
  className = "",
  tone = "default",
}: ThemeToggleProps) {
  const { theme, toggle } = useTheme();
  const isDark = theme === "dark";
  const onDark = tone === "onDark";

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      aria-pressed={isDark}
      className={
        "relative inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full " +
        "transition-colors duration-150 " +
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 " +
        (onDark
          ? "border border-white/35 bg-white/10 hover:border-white/55 hover:bg-white/18 "
          : "border border-border-soft hover:border-border-strong bg-bg-base hover:bg-bg-subtle ") +
        className
      }
    >
      <AnimatePresence initial={false} mode="wait">
        {isDark ? (
          <motion.span
            key="moon"
            initial={{ rotate: -180, opacity: 0 }}
            animate={{ rotate: 0, opacity: 1 }}
            exit={{ rotate: 180, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className={
              "absolute inset-0 flex items-center justify-center " +
              (onDark ? "text-white" : "text-text-primary")
            }
            aria-hidden="true"
          >
            <Moon className="h-[18px] w-[18px]" />
          </motion.span>
        ) : (
          <motion.span
            key="sun"
            initial={{ rotate: 180, opacity: 0 }}
            animate={{ rotate: 0, opacity: 1 }}
            exit={{ rotate: -180, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className={
              "absolute inset-0 flex items-center justify-center " +
              (onDark ? "text-white" : "text-text-primary")
            }
            aria-hidden="true"
          >
            <Sun className="h-[18px] w-[18px]" />
          </motion.span>
        )}
      </AnimatePresence>
    </button>
  );
}
